import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { categoryService, productService, variantService } from "../services";
import attributeService from "../services/attributeService";

const BULK_EDIT_SELECTION_STORAGE_KEY = "gala-admin-selected-products-bulk-edit";

const flattenCategories = (nodes, level = 0) => {
  if (!Array.isArray(nodes)) return [];

  return nodes.flatMap((node) => {
    const current = {
      _id: node._id,
      title: node.title,
      level,
    };

    const children = node.subcategories
      ? flattenCategories(node.subcategories, level + 1)
      : [];

    return [current, ...children];
  });
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const parseTagsInput = (value) => {
  const source = Array.isArray(value) ? value.join(",") : value || "";

  return source
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag, index, all) => tag.length > 0 && all.indexOf(tag) === index);
};

const buildVariantTitle = (variant) => {
  const attributes = ensureArray(variant?.attributes);
  const summary = attributes
    .map((attribute) => {
      if (Array.isArray(attribute?.values) && attribute.values.length > 0) {
        return `${attribute.attributeName}: ${attribute.values.join(", ")}`;
      }

      if (
        attribute?.value !== undefined &&
        attribute?.value !== null &&
        String(attribute.value).trim() !== ""
      ) {
        return `${attribute.attributeName}: ${attribute.value}`;
      }

      return attribute?.attributeName || "";
    })
    .filter(Boolean)
    .join(" | ");

  return summary || variant?.sku || "Variant";
};

const createEmptyAttributeValue = (definition) => ({
  attributeName: definition.name,
  attributeType: definition.inputType,
  value:
    definition.inputType === "multi-select"
      ? ""
      : definition.inputType === "boolean"
      ? ""
      : definition.inputType === "color-picker"
      ? ""
      : "",
  values: definition.inputType === "multi-select" ? [] : undefined,
  colorHex:
    definition.inputType === "color-picker" ? "#000000" : undefined,
});

const normalizeAttributePayload = (attributes) =>
  ensureArray(attributes).map((attribute) => ({
    attributeName: attribute.attributeName,
    attributeType: attribute.attributeType,
    value:
      attribute.attributeType === "number" && attribute.value !== ""
        ? Number(attribute.value)
        : attribute.attributeType === "boolean"
        ? attribute.value === true || attribute.value === "true"
        : attribute.value ?? "",
    values:
      attribute.attributeType === "multi-select"
        ? ensureArray(attribute.values)
        : undefined,
    colorHex:
      attribute.attributeType === "color-picker"
        ? attribute.colorHex || "#000000"
        : undefined,
  }));

const normalizeVariant = (variant) => ({
  _id: variant._id,
  sku: variant.sku || "",
  price: variant.priceOverride ?? 0,
  stock: variant.stock ?? 0,
  isActive: variant.isActive ?? true,
  isDefault: variant.isDefault || false,
  attributes: normalizeAttributePayload(variant.attributes || []),
  keyIngredients: ensureArray(variant.keyIngredients),
  howToUse: ensureArray(variant.howToUse).map((step) => ({
    step: step?.step || "",
    description: step?.description || "",
  })),
  existingImages: ensureArray(variant.images),
  existingVideo: variant.video || null,
});

const normalizeGroup = (product, variants, availableAttributes) => {
  const productDefaultVariantId =
    product?.defaultVariantId?._id || product?.defaultVariantId || null;

  const normalizedVariants = ensureArray(variants)
    .map(normalizeVariant)
    .sort((left, right) => {
      if (left._id === productDefaultVariantId) return -1;
      if (right._id === productDefaultVariantId) return 1;
      if (left.isDefault && !right.isDefault) return -1;
      if (!left.isDefault && right.isDefault) return 1;
      return 0;
    })
    .map((variant) => ({
      ...variant,
      isDefault:
        variant._id === productDefaultVariantId ||
        (productDefaultVariantId == null && variant.isDefault),
    }));

  return {
    _id: product._id,
    title: product.title || "",
    description: product.description || "",
    categoryId: product.categoryId?._id || product.categoryId || "",
    productCode: product.productCode || "",
    isTrending: product.isTrending || false,
    isActive: product.isActive || false,
    metaTitle: product.metaTitle || "",
    metaDescription: product.metaDescription || "",
    tagsInput: ensureArray(product.tags).join(", "),
    defaultVariantId:
      normalizedVariants.find((variant) => variant.isDefault)?._id || null,
    originalDefaultVariantId: productDefaultVariantId,
    variants: normalizedVariants,
    availableAttributes: ensureArray(availableAttributes),
  };
};

const summarizeAttributes = (attributes) => {
  const list = ensureArray(attributes);
  if (list.length === 0) {
    return "No attributes";
  }

  return list
    .map((attribute) => {
      if (attribute.attributeType === "multi-select") {
        return `${attribute.attributeName}: ${ensureArray(attribute.values).join(", ")}`;
      }

      if (attribute.attributeType === "color-picker") {
        return `${attribute.attributeName}: ${attribute.value || "Color"} ${
          attribute.colorHex || ""
        }`.trim();
      }

      return `${attribute.attributeName}: ${String(attribute.value ?? "")}`;
    })
    .join(" | ");
};

const summarizeVariantDetails = (variant) => {
  const ingredientCount = ensureArray(variant.keyIngredients).filter(Boolean).length;
  const howToUseCount = ensureArray(variant.howToUse).filter(
    (step) => step?.step || step?.description
  ).length;

  return `${ingredientCount} ingredients, ${howToUseCount} steps`;
};

const AttributeEditorDialog = ({
  open,
  variant,
  availableAttributes,
  onClose,
  onSave,
}) => {
  const [draftAttributes, setDraftAttributes] = useState([]);
  const [error, setError] = useState("");

  const definitions = useMemo(() => {
    const available = ensureArray(availableAttributes).map((attribute) => ({
      key: attribute._id || attribute.name,
      name: attribute.name,
      inputType: attribute.inputType,
      isRequired: !!attribute.isRequired,
      isLegacy: false,
      options: ensureArray(attribute.options),
    }));

    const existingNames = new Set(available.map((item) => item.name));
    const legacy = ensureArray(variant?.attributes)
      .filter((attribute) => !existingNames.has(attribute.attributeName))
      .map((attribute) => ({
        key: `legacy-${attribute.attributeName}`,
        name: attribute.attributeName,
        inputType: attribute.attributeType,
        isRequired: true,
        isLegacy: true,
        options: [],
      }));

    return [...available, ...legacy];
  }, [availableAttributes, variant]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const currentAttributes = ensureArray(variant?.attributes);
    const nextDraft = definitions.map((definition) => {
      const existing = currentAttributes.find(
        (attribute) => attribute.attributeName === definition.name
      );

      if (existing) {
        return {
          ...createEmptyAttributeValue(definition),
          attributeName: existing.attributeName,
          attributeType: existing.attributeType,
          value:
            existing.attributeType === "boolean"
              ? existing.value === true || existing.value === "true"
              : existing.value ?? "",
          values: ensureArray(existing.values),
          colorHex:
            existing.attributeType === "color-picker"
              ? existing.colorHex || "#000000"
              : undefined,
          enabled: true,
          isRequired: definition.isRequired,
          isLegacy: definition.isLegacy,
          options: definition.options,
        };
      }

      return {
        ...createEmptyAttributeValue(definition),
        enabled: definition.isRequired,
        isRequired: definition.isRequired,
        isLegacy: definition.isLegacy,
        options: definition.options,
      };
    });

    setDraftAttributes(nextDraft);
    setError("");
  }, [definitions, open, variant]);

  const updateAttribute = (attributeName, patch) => {
    setDraftAttributes((current) =>
      current.map((attribute) =>
        attribute.attributeName === attributeName
          ? { ...attribute, ...patch }
          : attribute
      )
    );
  };

  const handleSave = () => {
    const enabledAttributes = draftAttributes.filter((attribute) => attribute.enabled);

    for (const attribute of enabledAttributes) {
      if (attribute.attributeType === "multi-select") {
        if (ensureArray(attribute.values).length === 0) {
          setError(`${attribute.attributeName} needs at least one value.`);
          return;
        }
        continue;
      }

      if (attribute.attributeType === "color-picker") {
        if (!String(attribute.value || "").trim()) {
          setError(`${attribute.attributeName} needs a color name.`);
          return;
        }

        if (!attribute.colorHex) {
          setError(`${attribute.attributeName} needs a color code.`);
          return;
        }
        continue;
      }

      if (attribute.attributeType === "boolean") {
        if (attribute.value !== true && attribute.value !== false) {
          setError(`${attribute.attributeName} needs a Yes/No value.`);
          return;
        }
        continue;
      }

      if (String(attribute.value ?? "").trim() === "") {
        setError(`${attribute.attributeName} cannot be empty.`);
        return;
      }
    }

    setError("");
    onSave(
      enabledAttributes.map((attribute) => ({
        attributeName: attribute.attributeName,
        attributeType: attribute.attributeType,
        value:
          attribute.attributeType === "number"
            ? attribute.value === ""
              ? ""
              : Number(attribute.value)
            : attribute.value,
        values:
          attribute.attributeType === "multi-select"
            ? ensureArray(attribute.values)
            : undefined,
        colorHex:
          attribute.attributeType === "color-picker"
            ? attribute.colorHex || "#000000"
            : undefined,
      }))
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Edit Attributes{variant ? ` for ${buildVariantTitle(variant)}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {draftAttributes.length === 0 ? (
            <Alert severity="info">
              No attributes are configured for this product yet.
            </Alert>
          ) : null}
          {draftAttributes.map((attribute) => (
            <Paper
              key={attribute.attributeName}
              variant="outlined"
              sx={{ p: 2 }}
            >
              <Stack spacing={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                >
                  <Box>
                    <Typography variant="subtitle2">
                      {attribute.attributeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {attribute.attributeType}
                      {attribute.isLegacy ? " | Legacy attribute" : ""}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={attribute.enabled}
                        disabled={attribute.isRequired || attribute.isLegacy}
                        onChange={(event) =>
                          updateAttribute(attribute.attributeName, {
                            enabled: event.target.checked,
                          })
                        }
                      />
                    }
                    label={attribute.isRequired ? "Required" : "Include"}
                  />
                </Box>

                {attribute.enabled ? (
                  <>
                    {attribute.attributeType === "select" ? (
                      <FormControl fullWidth>
                        <InputLabel>Value</InputLabel>
                        <Select
                          value={attribute.value ?? ""}
                          label="Value"
                          onChange={(event) =>
                            updateAttribute(attribute.attributeName, {
                              value: event.target.value,
                            })
                          }
                        >
                          {ensureArray(attribute.options).map((option) => (
                            <MenuItem key={option._id || option.value} value={option.value}>
                              {option.displayValue || option.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}

                    {attribute.attributeType === "multi-select" ? (
                      <FormControl fullWidth>
                        <InputLabel>Values</InputLabel>
                        <Select
                          multiple
                          value={ensureArray(attribute.values)}
                          label="Values"
                          onChange={(event) =>
                            updateAttribute(attribute.attributeName, {
                              values: event.target.value,
                            })
                          }
                        >
                          {ensureArray(attribute.options).map((option) => (
                            <MenuItem key={option._id || option.value} value={option.value}>
                              {option.displayValue || option.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}

                    {attribute.attributeType === "text" ? (
                      <TextField
                        fullWidth
                        label="Value"
                        value={attribute.value ?? ""}
                        onChange={(event) =>
                          updateAttribute(attribute.attributeName, {
                            value: event.target.value,
                          })
                        }
                      />
                    ) : null}

                    {attribute.attributeType === "number" ? (
                      <TextField
                        fullWidth
                        type="number"
                        label="Value"
                        value={attribute.value ?? ""}
                        onChange={(event) =>
                          updateAttribute(attribute.attributeName, {
                            value: event.target.value,
                          })
                        }
                      />
                    ) : null}

                    {attribute.attributeType === "boolean" ? (
                      <FormControl fullWidth>
                        <InputLabel>Value</InputLabel>
                        <Select
                          value={
                            attribute.value === true
                              ? "true"
                              : attribute.value === false
                              ? "false"
                              : ""
                          }
                          label="Value"
                          onChange={(event) =>
                            updateAttribute(attribute.attributeName, {
                              value: event.target.value === "true",
                            })
                          }
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </FormControl>
                    ) : null}

                    {attribute.attributeType === "color-picker" ? (
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <TextField
                          fullWidth
                          label="Color Name"
                          value={attribute.value ?? ""}
                          onChange={(event) =>
                            updateAttribute(attribute.attributeName, {
                              value: event.target.value,
                            })
                          }
                          sx={{ flex: 1, minWidth: 220 }}
                        />
                        <TextField
                          label="Color"
                          type="color"
                          value={attribute.colorHex || "#000000"}
                          onChange={(event) =>
                            updateAttribute(attribute.attributeName, {
                              colorHex: event.target.value,
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 120 }}
                        />
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Attributes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VariantDetailsDialog = ({ open, variant, onClose, onSave }) => {
  const [keyIngredients, setKeyIngredients] = useState([]);
  const [howToUse, setHowToUse] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setKeyIngredients(
      ensureArray(variant?.keyIngredients).length > 0
        ? ensureArray(variant.keyIngredients)
        : [""]
    );
    setHowToUse(
      ensureArray(variant?.howToUse).length > 0
        ? ensureArray(variant.howToUse).map((step) => ({
            step: step?.step || "",
            description: step?.description || "",
          }))
        : [{ step: "", description: "" }]
    );
    setError("");
  }, [open, variant]);

  const handleSave = () => {
    const normalizedIngredients = keyIngredients
      .map((ingredient) => ingredient.trim())
      .filter(Boolean);

    const normalizedSteps = howToUse
      .map((step) => ({
        step: step.step.trim(),
        description: step.description.trim(),
      }))
      .filter((step) => step.step || step.description);

    const invalidStep = normalizedSteps.find((step) => !step.step);
    if (invalidStep) {
      setError("Each usage row needs a step title.");
      return;
    }

    setError("");
    onSave({
      keyIngredients: normalizedIngredients,
      howToUse: normalizedSteps,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Edit Variant Details{variant ? ` for ${buildVariantTitle(variant)}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">Key Ingredients</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setKeyIngredients((current) => [...current, ""])}
              >
                Add
              </Button>
            </Box>
            <Stack spacing={1.5}>
              {keyIngredients.map((ingredient, index) => (
                <Box key={`ingredient-${index}`} display="flex" gap={1}>
                  <TextField
                    fullWidth
                    label={`Ingredient ${index + 1}`}
                    value={ingredient}
                    onChange={(event) =>
                      setKeyIngredients((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? event.target.value : item
                        )
                      )
                    }
                  />
                  <IconButton
                    color="error"
                    onClick={() =>
                      setKeyIngredients((current) =>
                        current.length === 1
                          ? [""]
                          : current.filter((_, itemIndex) => itemIndex !== index)
                      )
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">How To Use</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() =>
                  setHowToUse((current) => [
                    ...current,
                    { step: "", description: "" },
                  ])
                }
              >
                Add Step
              </Button>
            </Box>
            <Stack spacing={2}>
              {howToUse.map((step, index) => (
                <Paper key={`step-${index}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label={`Step ${index + 1}`}
                      value={step.step}
                      onChange={(event) =>
                        setHowToUse((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, step: event.target.value }
                              : item
                          )
                        )
                      }
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Description"
                      value={step.description}
                      onChange={(event) =>
                        setHowToUse((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, description: event.target.value }
                              : item
                          )
                        )
                      }
                    />
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        color="error"
                        onClick={() =>
                          setHowToUse((current) =>
                            current.length === 1
                              ? [{ step: "", description: "" }]
                              : current.filter(
                                  (_, itemIndex) => itemIndex !== index
                                )
                          )
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkEditProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attributeDialogState, setAttributeDialogState] = useState({
    open: false,
    productId: null,
    variantId: null,
  });
  const [detailsDialogState, setDetailsDialogState] = useState({
    open: false,
    productId: null,
    variantId: null,
  });

  const selectedProductIds = useMemo(() => {
    const stateIds = ensureArray(location.state?.productIds).filter(Boolean);
    if (stateIds.length > 0) {
      return [...new Set(stateIds)];
    }

    try {
      const storedIds = JSON.parse(
        sessionStorage.getItem(BULK_EDIT_SELECTION_STORAGE_KEY) || "[]"
      );
      return ensureArray(storedIds).filter(Boolean);
    } catch {
      return [];
    }
  }, [location.state?.productIds]);

  const loadData = useCallback(async () => {
    if (selectedProductIds.length === 0) {
      setGroups([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const categoryPromise = categoryService.getHierarchical();
      const productGroupPromises = selectedProductIds.map(async (productId) => {
        const [product, variantResponse, availableAttributes] = await Promise.all([
          productService.getProductById(productId),
          variantService.getVariants({
            productId,
            page: 1,
            limit: 250,
            sort: "createdAt",
            order: "asc",
          }),
          attributeService.getAttributesForProduct(productId),
        ]);

        return normalizeGroup(
          product,
          ensureArray(variantResponse?.variants),
          availableAttributes
        );
      });

      const [hierarchicalCategories, nextGroups] = await Promise.all([
        categoryPromise,
        Promise.all(productGroupPromises),
      ]);

      setCategoryOptions(flattenCategories(hierarchicalCategories));
      setGroups(nextGroups);
    } catch (error) {
      console.error("Failed to load bulk editor data:", error);
      toast.error("Failed to load bulk edit data");
    } finally {
      setLoading(false);
    }
  }, [selectedProductIds]);

  useEffect(() => {
    if (selectedProductIds.length > 0) {
      sessionStorage.setItem(
        BULK_EDIT_SELECTION_STORAGE_KEY,
        JSON.stringify(selectedProductIds)
      );
    }
  }, [selectedProductIds]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateGroup = (productId, patch) => {
    setGroups((current) =>
      current.map((group) =>
        group._id === productId ? { ...group, ...patch } : group
      )
    );
  };

  const updateVariant = (productId, variantId, patch) => {
    setGroups((current) =>
      current.map((group) => {
        if (group._id !== productId) {
          return group;
        }

        return {
          ...group,
          variants: group.variants.map((variant) =>
            variant._id === variantId ? { ...variant, ...patch } : variant
          ),
        };
      })
    );
  };

  const handleDefaultVariantChange = (productId, variantId) => {
    setGroups((current) =>
      current.map((group) => {
        if (group._id !== productId) {
          return group;
        }

        return {
          ...group,
          defaultVariantId: variantId,
          variants: group.variants.map((variant) => ({
            ...variant,
            isDefault: variant._id === variantId,
            isActive: variant._id === variantId ? true : variant.isActive,
          })),
        };
      })
    );
  };

  const handleVariantActiveChange = (productId, variantId, checked) => {
    setGroups((current) =>
      current.map((group) => {
        if (group._id !== productId) {
          return group;
        }

        const targetVariant = group.variants.find((variant) => variant._id === variantId);
        if (!targetVariant) {
          return group;
        }

        if (!checked && targetVariant.isDefault) {
          const fallbackVariant = group.variants.find(
            (variant) => variant._id !== variantId
          );

          if (!fallbackVariant) {
            toast.error("A product needs at least one active default variant.");
            return group;
          }

          return {
            ...group,
            defaultVariantId: fallbackVariant._id,
            variants: group.variants.map((variant) => {
              if (variant._id === variantId) {
                return {
                  ...variant,
                  isActive: false,
                  isDefault: false,
                };
              }

              if (variant._id === fallbackVariant._id) {
                return {
                  ...variant,
                  isActive: true,
                  isDefault: true,
                };
              }

              return {
                ...variant,
                isDefault: false,
              };
            }),
          };
        }

        return {
          ...group,
          variants: group.variants.map((variant) =>
            variant._id === variantId
              ? { ...variant, isActive: checked }
              : variant
          ),
        };
      })
    );
  };

  const attributeDialogContext = useMemo(() => {
    if (!attributeDialogState.open) {
      return { group: null, variant: null };
    }

    const group = groups.find((item) => item._id === attributeDialogState.productId) || null;
    const variant =
      group?.variants.find((item) => item._id === attributeDialogState.variantId) || null;

    return { group, variant };
  }, [attributeDialogState, groups]);

  const detailsDialogContext = useMemo(() => {
    if (!detailsDialogState.open) {
      return { group: null, variant: null };
    }

    const group = groups.find((item) => item._id === detailsDialogState.productId) || null;
    const variant =
      group?.variants.find((item) => item._id === detailsDialogState.variantId) || null;

    return { group, variant };
  }, [detailsDialogState, groups]);

  const validateGroups = () => {
    for (const group of groups) {
      if (!group.title.trim() || group.title.trim().length < 2) {
        throw new Error(`"${group.productCode || group.title}" needs a valid title.`);
      }

      if (!group.description.trim() || group.description.trim().length < 10) {
        throw new Error(`"${group.title}" needs a description of at least 10 characters.`);
      }

      if (!group.categoryId) {
        throw new Error(`"${group.title}" needs a category.`);
      }

      if (group.variants.length === 0) {
        throw new Error(`"${group.title}" does not have any variants to update.`);
      }

      const defaultVariant = group.variants.find((variant) => variant.isDefault);
      if (!defaultVariant) {
        throw new Error(`"${group.title}" needs one default variant selected.`);
      }

      if (!defaultVariant.isActive) {
        throw new Error(`The default variant for "${group.title}" must stay active.`);
      }

      for (const variant of group.variants) {
        if (!variant.sku.trim()) {
          throw new Error(`A variant in "${group.title}" is missing SKU.`);
        }

        if (variant.price === "" || Number.isNaN(Number(variant.price)) || Number(variant.price) < 0) {
          throw new Error(`"${buildVariantTitle(variant)}" needs a valid price.`);
        }

        if (
          variant.stock === "" ||
          Number.isNaN(Number(variant.stock)) ||
          Number(variant.stock) < 0
        ) {
          throw new Error(`"${buildVariantTitle(variant)}" needs valid stock.`);
        }

        const normalizedAttributes = normalizeAttributePayload(variant.attributes);
        if (normalizedAttributes.length === 0) {
          throw new Error(`"${buildVariantTitle(variant)}" needs at least one attribute.`);
        }
      }
    }
  };

  const handleSaveAll = async () => {
    try {
      validateGroups();
      setSaving(true);

      for (const group of groups) {
        await productService.updateProduct(group._id, {
          title: group.title.trim(),
          description: group.description.trim(),
          categoryId: group.categoryId,
          productCode: group.productCode,
          isActive: group.isActive,
          isTrending: !!group.isTrending,
          metaTitle: group.metaTitle.trim(),
          metaDescription: group.metaDescription.trim(),
          tags: parseTagsInput(group.tagsInput),
        });

        for (const variant of group.variants) {
          await variantService.updateVariant(variant._id, {
            attributes: normalizeAttributePayload(variant.attributes),
            price: Number(variant.price),
            stock: Number(variant.stock),
            sku: variant.sku.trim(),
            isActive: !!variant.isActive,
            keyIngredients: ensureArray(variant.keyIngredients),
            howToUse: ensureArray(variant.howToUse),
            existingImages: ensureArray(variant.existingImages),
            existingVideo: variant.existingVideo,
          });
        }

        const selectedDefaultVariant = group.variants.find((variant) => variant.isDefault);
        if (
          selectedDefaultVariant &&
          selectedDefaultVariant._id !== group.originalDefaultVariantId
        ) {
          await variantService.setAsDefault(selectedDefaultVariant._id);
        }
      }

      toast.success("Bulk product updates saved successfully");
      await loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save bulk product updates"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={2}
        flexWrap="wrap"
        mb={3}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/products")}
            sx={{ mb: 1 }}
          >
            Back to Products
          </Button>
          <Typography variant="h4">Bulk Edit Products</Typography>
          <Typography variant="body2" color="text.secondary">
            Product rows come first, then each product&apos;s variants underneath.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={saving || groups.length === 0}
        >
          {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </Box>

      {groups.length === 0 ? (
        <Alert severity="info">
          No selected products were found. Choose products from the products list
          first, then open bulk edit.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 1400 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 100 }}>Row</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Title / SKU</TableCell>
                <TableCell sx={{ minWidth: 320 }}>Description / Attributes</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Category / Price</TableCell>
                <TableCell sx={{ minWidth: 220 }}>SEO / Stock</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Tags / Status</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Variant Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <React.Fragment key={group._id}>
                  <TableRow sx={{ backgroundColor: "rgba(25, 118, 210, 0.05)" }}>
                    <TableCell>
                      <Chip label="Product" color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Title"
                          value={group.title}
                          onChange={(event) =>
                            updateGroup(group._id, { title: event.target.value })
                          }
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Product Code"
                          value={group.productCode}
                          size="small"
                          fullWidth
                          disabled
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Description"
                          value={group.description}
                          onChange={(event) =>
                            updateGroup(group._id, {
                              description: event.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          multiline
                          minRows={4}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1.5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={group.categoryId}
                            label="Category"
                            onChange={(event) =>
                              updateGroup(group._id, {
                                categoryId: event.target.value,
                              })
                            }
                          >
                            {categoryOptions.map((category) => (
                              <MenuItem
                                key={category._id}
                                value={category._id}
                                sx={{ pl: category.level > 0 ? category.level * 2 : 1 }}
                              >
                                {category.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={group.isTrending}
                              onChange={(event) =>
                                updateGroup(group._id, {
                                  isTrending: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Trending"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Meta Title"
                          value={group.metaTitle}
                          onChange={(event) =>
                            updateGroup(group._id, {
                              metaTitle: event.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Meta Description"
                          value={group.metaDescription}
                          onChange={(event) =>
                            updateGroup(group._id, {
                              metaDescription: event.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          multiline
                          minRows={3}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Tags"
                          value={group.tagsInput}
                          onChange={(event) =>
                            updateGroup(group._id, {
                              tagsInput: event.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          placeholder="comma, separated, tags"
                        />
                        <Chip
                          label={group.isActive ? "Published" : "Draft"}
                          color={group.isActive ? "success" : "default"}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {group.variants.length} variant{group.variants.length === 1 ? "" : "s"}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {group.variants.map((variant) => (
                    <TableRow key={variant._id}>
                      <TableCell>
                        <Chip label="Variant" size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1.5}>
                          <TextField
                            label="SKU"
                            value={variant.sku}
                            onChange={(event) =>
                              updateVariant(group._id, variant._id, {
                                sku: event.target.value,
                              })
                            }
                            size="small"
                            fullWidth
                          />
                          <Typography variant="caption" color="text.secondary">
                            {buildVariantTitle(variant)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {summarizeAttributes(variant.attributes)}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() =>
                              setAttributeDialogState({
                                open: true,
                                productId: group._id,
                                variantId: variant._id,
                              })
                            }
                          >
                            Edit Attributes
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Price"
                            type="number"
                            value={variant.price}
                            onChange={(event) =>
                              updateVariant(group._id, variant._id, {
                                price: event.target.value,
                              })
                            }
                            size="small"
                            fullWidth
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Stock"
                            type="number"
                            value={variant.stock}
                            onChange={(event) =>
                              updateVariant(group._id, variant._id, {
                                stock: event.target.value,
                              })
                            }
                            size="small"
                            fullWidth
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={variant.isDefault}
                                onChange={() =>
                                  handleDefaultVariantChange(group._id, variant._id)
                                }
                              />
                            }
                            label="Default"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1.5}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={variant.isActive}
                                onChange={(event) =>
                                  handleVariantActiveChange(
                                    group._id,
                                    variant._id,
                                    event.target.checked
                                  )
                                }
                              />
                            }
                            label={variant.isActive ? "Active" : "Inactive"}
                          />
                          <Chip
                            label={variant.isDefault ? "Default Variant" : "Optional Variant"}
                            color={variant.isDefault ? "primary" : "default"}
                            size="small"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {summarizeVariantDetails(variant)}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() =>
                              setDetailsDialogState({
                                open: true,
                                productId: group._id,
                                variantId: variant._id,
                              })
                            }
                          >
                            Edit Details
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AttributeEditorDialog
        open={attributeDialogState.open}
        variant={attributeDialogContext.variant}
        availableAttributes={attributeDialogContext.group?.availableAttributes || []}
        onClose={() =>
          setAttributeDialogState({ open: false, productId: null, variantId: null })
        }
        onSave={(nextAttributes) => {
          if (!attributeDialogContext.group || !attributeDialogContext.variant) {
            return;
          }

          updateVariant(attributeDialogContext.group._id, attributeDialogContext.variant._id, {
            attributes: nextAttributes,
          });
          setAttributeDialogState({ open: false, productId: null, variantId: null });
        }}
      />

      <VariantDetailsDialog
        open={detailsDialogState.open}
        variant={detailsDialogContext.variant}
        onClose={() =>
          setDetailsDialogState({ open: false, productId: null, variantId: null })
        }
        onSave={(nextDetails) => {
          if (!detailsDialogContext.group || !detailsDialogContext.variant) {
            return;
          }

          updateVariant(detailsDialogContext.group._id, detailsDialogContext.variant._id, {
            keyIngredients: nextDetails.keyIngredients,
            howToUse: nextDetails.howToUse,
          });
          setDetailsDialogState({ open: false, productId: null, variantId: null });
        }}
      />
    </Box>
  );
};

export default BulkEditProducts;
